const scheduledResizes = new WeakMap();

const getScheduler = () => {
	if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
		return {
			schedule: window.requestAnimationFrame.bind(window),
			cancel: typeof window.cancelAnimationFrame === 'function'
				? window.cancelAnimationFrame.bind(window)
				: null,
		};
	}

	return {
		schedule: (callback) => setTimeout(callback, 0),
		cancel: (id) => clearTimeout(id),
	};
};

export const resizeTextarea = (textarea) => {
	if (!textarea?.style) return;

	textarea.style.height = 'auto';
	const borderHeight = Math.max(0, textarea.offsetHeight - textarea.clientHeight);
	textarea.style.height = `${textarea.scrollHeight + borderHeight}px`;
};

export const scheduleTextareaResize = (textarea) => {
	if (!textarea) return;

	const scheduler = getScheduler();
	const pending = scheduledResizes.get(textarea);
	if (pending !== undefined && scheduler.cancel) scheduler.cancel(pending);

	const scheduled = scheduler.schedule(() => {
		scheduledResizes.delete(textarea);
		resizeTextarea(textarea);
	});
	scheduledResizes.set(textarea, scheduled);
};

const teardownTextareaAutosize = (textarea, { resetHeight = false } = {}) => {
	if (textarea.__nocTextareaAutosize) {
		textarea.removeEventListener('input', textarea.__nocTextareaAutosize);
		delete textarea.__nocTextareaAutosize;
	}
	textarea.__nocTextareaResizeObserver?.disconnect?.();
	delete textarea.__nocTextareaResizeObserver;

	const scheduler = getScheduler();
	const pending = scheduledResizes.get(textarea);
	if (pending !== undefined && scheduler.cancel) scheduler.cancel(pending);
	scheduledResizes.delete(textarea);

	if (resetHeight && textarea?.style) textarea.style.height = '';
};

const mountTextareaAutosize = (textarea) => {
	if (textarea.__nocTextareaAutosize) return;

	const resize = () => scheduleTextareaResize(textarea);
	textarea.__nocTextareaAutosize = resize;
	textarea.addEventListener('input', resize);
	if (typeof ResizeObserver === 'function') {
		let previousWidth = textarea.getBoundingClientRect?.().width ?? textarea.clientWidth;
		const observer = new ResizeObserver((entries) => {
			const width = entries?.[0]?.contentRect?.width ?? textarea.clientWidth;
			if (width === previousWidth) return;
			previousWidth = width;
			resize();
		});
		observer.observe(textarea);
		textarea.__nocTextareaResizeObserver = observer;
	}
	resize();
};

const autosizeEnabled = (binding) => binding?.value !== false;

export const vTextareaAutosize = {
	mounted(textarea, binding) {
		if (autosizeEnabled(binding)) mountTextareaAutosize(textarea);
	},
	updated(textarea, binding) {
		if (!autosizeEnabled(binding)) {
			teardownTextareaAutosize(textarea, { resetHeight: true });
			return;
		}

		mountTextareaAutosize(textarea);
		scheduleTextareaResize(textarea);
	},
	unmounted(textarea) {
		teardownTextareaAutosize(textarea);
	},
};
